import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCoachBar } from './CoachBarProvider';
import { useCoachPanelContext } from './CoachPanelProvider';
import { isCoachHiddenRoute } from './coachRouteUtils';
import { useCoachOptIn } from '@/hooks/useCoachOptIn';
import { EnhancedCoachingOptInModal } from './EnhancedCoachingOptInModal';
import { Button } from '@/components/ui/button';
import { MessageCircle, X, Lock, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

const TOOLTIP_STORAGE_KEY = 'momentum-coach-tooltip-seen';

export const FloatingCoachBar = () => {
  const { currentInsight, dismiss, autoDismiss, isVisible } = useCoachBar();
  const navigate = useNavigate();
  const coachPanel = useCoachPanelContext();
  const { modalOpen, setModalOpen } = useCoachOptIn();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Auto-dismiss non-persistent insights after 12 seconds.
  // Uses autoDismiss (no cooldown) instead of dismiss (2h cooldown).
  useEffect(() => {
    if (!isVisible || !currentInsight || currentInsight.persistent) return;

    const timer = setTimeout(() => {
      autoDismiss();
    }, 12000);

    return () => clearTimeout(timer);
  }, [isVisible, currentInsight?.type, currentInsight?.persistent, autoDismiss]);

  // First-time tooltip
  useEffect(() => {
    if (!isVisible) return;
    const seen = localStorage.getItem(TOOLTIP_STORAGE_KEY);
    if (!seen) {
      setShowTooltip(true);
      const timer = setTimeout(() => {
        setShowTooltip(false);
        localStorage.setItem(TOOLTIP_STORAGE_KEY, 'true');
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const dismissTooltip = useCallback(() => {
    setShowTooltip(false);
    localStorage.setItem(TOOLTIP_STORAGE_KEY, 'true');
  }, []);

  const location = useLocation();
  if (isCoachHiddenRoute(location.pathname) || !isVisible || !currentInsight) return null;

  const isLocked = currentInsight.type === 'onboarding_locked';
  const isUnlocking = currentInsight.type === 'onboarding_unlocking';
  const isOnboarding = isLocked || isUnlocking;

  const handleAction = () => {
    dismissTooltip();
    if (currentInsight.action.tab === 'enhanced_coaching') {
      setModalOpen(true);
      return;
    }
    if (currentInsight.action.tab === 'coach_panel') {
      coachPanel.open();
    } else if (currentInsight.action.tab === 'mentorship') {
      navigate('/network');
    } else {
      navigate('/dashboard', { state: { activeTab: currentInsight.action.tab } });
    }
    if (!currentInsight.persistent) {
      dismiss();
    }
  };

  return (
    <div
      className={cn(
        'fixed bottom-4 left-4 right-4 z-40 mx-auto max-w-lg pointer-events-none',
        'animate-in slide-in-from-bottom-4 fade-in duration-300',
      )}
    >
      {/* First-time tooltip — hide when any Radix dialog/drawer is open */}
      {showTooltip && !isCollapsed && !document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [data-radix-popper-content-wrapper]') && (
        <div
          className="pointer-events-auto mb-2 mx-auto max-w-sm rounded-lg bg-foreground text-background px-4 py-2 text-sm text-center shadow-lg cursor-pointer"
          onClick={dismissTooltip}
        >
          Your AI coach is here — ask anything about your goals or values.
        </div>
      )}

      {isCollapsed ? (
        /* Collapsed: compact bar with icon + expand toggle */
        <div className="flex items-center justify-center pointer-events-auto">
          <button
            onClick={() => { setIsCollapsed(false); dismissTooltip(); }}
            className={cn(
              'flex items-center gap-2 rounded-full border shadow-lg px-4 py-2 transition-colors',
              isLocked && 'border-muted bg-muted/80 backdrop-blur-sm',
              isUnlocking && 'border-primary/30 bg-card/95 backdrop-blur-sm',
              !isOnboarding && 'border-primary/20 bg-card/95 backdrop-blur-sm',
            )}
          >
            <div
              className={cn(
                'shrink-0 w-6 h-6 rounded-full flex items-center justify-center',
                isLocked && 'bg-muted-foreground/10',
                isUnlocking && 'bg-primary/15',
                !isOnboarding && 'bg-primary/10',
              )}
            >
              {isLocked ? (
                <Lock className="w-3 h-3 text-muted-foreground" />
              ) : isUnlocking ? (
                <Sparkles className="w-3 h-3 text-primary animate-pulse" />
              ) : (
                <MessageCircle className="w-3 h-3 text-primary" />
              )}
            </div>
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ) : (
        /* Expanded: full bar */
        <div
          className={cn(
            'flex items-center gap-3 rounded-xl border shadow-lg px-4 py-3 pointer-events-auto',
            isLocked && 'border-muted bg-muted/80 backdrop-blur-sm',
            isUnlocking && 'border-primary/30 bg-card/95 backdrop-blur-sm',
            !isOnboarding && 'border-primary/20 bg-card/95 backdrop-blur-sm',
          )}
        >
          <div
            className={cn(
              'shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
              isLocked && 'bg-muted-foreground/10',
              isUnlocking && 'bg-primary/15',
              !isOnboarding && 'bg-primary/10',
            )}
          >
            {isLocked ? (
              <Lock className="w-4 h-4 text-muted-foreground" />
            ) : isUnlocking ? (
              <Sparkles className="w-4 h-4 text-primary animate-pulse" />
            ) : (
              <MessageCircle className="w-4 h-4 text-primary" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <p
              className={cn(
                'text-sm min-w-0 line-clamp-2',
                isLocked ? 'text-muted-foreground' : 'text-foreground',
              )}
            >
              {currentInsight.message}
            </p>
            {isUnlocking && (
              <div className="flex gap-1 mt-1.5">
                <div className="h-1 w-8 rounded-full bg-primary" />
                <div className="h-1 w-8 rounded-full bg-muted" />
              </div>
            )}
          </div>

          <Button
            size="sm"
            variant={isLocked ? 'outline' : 'default'}
            className="shrink-0 min-h-[44px] px-3"
            onClick={handleAction}
          >
            {currentInsight.action.label}
          </Button>

          {/* Collapse toggle */}
          <button
            onClick={() => setIsCollapsed(true)}
            className="shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md hover:bg-muted transition-colors"
            aria-label="Minimize coach bar"
          >
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </button>

          {!currentInsight.persistent && (
            <button
              onClick={dismiss}
              className="shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md hover:bg-muted transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>
      )}
      <EnhancedCoachingOptInModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
};
